import axios from "axios";
import CryptoJS from "crypto-js";
import { erpFetch, getErpAuthState, isErpApiSuccess } from "../erpClient";
import { getChargeTypeCode } from "../utils/otherChargeOptions";

export { getChargeTypeCode };

// Secret key for encryption (loaded from environment variables)
// const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
const SECRET_KEY = (process.env.REACT_APP_SECRET_KEY || "").trim();

// Helper to safely stringify JSON and remove circular references/Socket objects
const safeStringify = (data) => {
  const seen = new WeakSet();
  return JSON.stringify(data, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return; // Circular reference found, discard key
      }
      seen.add(value);

      // Explicitly remove Socket objects to prevent "Converting circular structure to JSON" errors
      if (
        value.constructor &&
        (value.constructor.name === "Socket" ||
          value.constructor.name === "ServerSocket")
      ) {
        return;
      }
    }
    return value;
  });
};

// Encrypt data with AES + IV
export const encryptData = (data) => {
  const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
  const iv = CryptoJS.lib.WordArray.random(16); // 16-byte IV

  const encrypted = CryptoJS.AES.encrypt(safeStringify(data), key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
  const cipherBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);

  return btoa(ivBase64 + "::" + cipherBase64);
};

// Decrypt data
export const decryptData = (encrypted) => {
  try {
    if (!encrypted || typeof encrypted !== "string") {
      throw new Error("Encrypted data is invalid or empty");
    }

    const combined = atob(encrypted);
    const parts = combined.split("::");
    if (parts.length !== 2) {
      throw new Error("Encrypted data format is invalid");
    }

    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    const ciphertext = CryptoJS.enc.Base64.parse(parts[1]);
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);

    const decrypted = CryptoJS.AES.decrypt({ ciphertext }, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) throw new Error("Failed to decrypt");

    return JSON.parse(decryptedText);
  } catch (error) {
    // console.error("Decryption error:", error.message);
    // console.error("Encrypted input:", encrypted);
    return null;
  }
};

// Get token from localStorage with decryption
export const getToken = () => {
  const encryptedToken = localStorage.getItem("token");
  return encryptedToken ? decryptData(encryptedToken) : null;
};

// API URLs (Legacy - commented out, using env variables below)
// These old hardcoded URLs are kept for reference only

// main
// export const apiUrl = "https://jsoft-api.gsdev.in/";
// export const imageBaseUrl = "https://jsoft-api.gsdev.in/img/product";
// export const imageCustomOrderUrl = "https://jsoft-api.gsdev.in/img/custom_order";
// export const imageMyProfileUrl = "https://jsoft-api.gsdev.in/img/profile_img/";

// test
// export const apiUrl = "https://jewellery-erp-test.gsdev.in/";
// export const imageBaseUrl = "https://jewellery-erp-test.gsdev.in/img/product";
// export const imageCustomOrderUrl = "https://jewellery-erp-test.gsdev.in/img/custom_order";
// export const imageMyProfileUrl = "https://jewellery-erp-test.gsdev.in/img/profile_img/";

const proxy = "https://cors-anywhere.herokuapp.com/";

// State/City API
export const stateCityApiConfig = {
  baseUrl: "https://api.countrystatecity.in/v1",
  apiKey: "NHhvOEcyWk50N2Vna3VFTE00bFp3MjFKR0ZEOUhkZlg4RTk1MlJlaA==",
  countryCode: "IN",
};

// Dynamic API URL based on PIN selection
// Initialize from localStorage or default to "access"
let currentPinType = localStorage.getItem("currentPinType") || "access";
let currentApiUrl =
  currentPinType === "quote"
    ? process.env.REACT_APP_QUOTE_API_URL
    : process.env.REACT_APP_API_URL;

// Function to switch API URL based on PIN
export const switchApiUrl = (pinType) => {
  // Check if we're already using this PIN type
  if (currentPinType === pinType) {
    // console.log(`Already using ${pinType} API, no switch needed`);
    return; // Don't dispatch event if no change
  }

  if (pinType === "quote") {
    currentApiUrl = process.env.REACT_APP_QUOTE_API_URL;
  } else {
    currentApiUrl = process.env.REACT_APP_API_URL;
  }

  // Update the apiClient baseURL
  apiClient.defaults.baseURL = currentApiUrl;
  currentPinType = pinType;

  // Store in localStorage to persist across page reloads
  localStorage.setItem("currentPinType", pinType);

  // console.log(`API URL switched to: ${currentApiUrl} (PIN type: ${pinType})`);

  // Emit a custom event to notify components about the API switch
  window.dispatchEvent(
    new CustomEvent("apiUrlChanged", {
      detail: { pinType, apiUrl: currentApiUrl },
    }),
  );
};

// Export dynamic API URL getter
export const getNodeApiUrl = () => currentApiUrl;

// Export function to get current PIN type
export const getCurrentPinType = () => currentPinType;

/** Quote-mode list scope on Invoice: "quotation" → type list; "all" → type mixed. */
export const QUOTE_INVOICE_LIST_SCOPE_KEY = "quoteInvoiceListScope";
export const QUOTE_INVOICE_LIST_SCOPE_EVENT = "quoteInvoiceListScopeChanged";

export const getQuoteInvoiceListScope = () => {
  try {
    return localStorage.getItem(QUOTE_INVOICE_LIST_SCOPE_KEY) === "all"
      ? "all"
      : "quotation";
  } catch {
    return "quotation";
  }
};

export const setQuoteInvoiceListScope = (scope) => {
  const next = scope === "all" ? "all" : "quotation";
  try {
    localStorage.setItem(QUOTE_INVOICE_LIST_SCOPE_KEY, next);
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(
      new CustomEvent(QUOTE_INVOICE_LIST_SCOPE_EVENT, {
        detail: { scope: next },
      }),
    );
  } catch {
    // ignore
  }
  return next;
};

/**
 * Quote PIN + navbar "All" → API `type: "mixed"`; otherwise `defaultType` (e.g. list/all).
 */
export const resolveQuoteListApiType = (defaultType = "list") => {
  if (
    String(getCurrentPinType() || "").toLowerCase() === "quote" &&
    getQuoteInvoiceListScope() === "all"
  ) {
    return "mixed";
  }
  return defaultType || "list";
};

/**
 * Quote PIN + navbar "All" → ledger/rojmel `book: "mixed"`; quotation + GST leave undefined.
 */
export const resolveQuoteLedgerBook = () => {
  if (
    String(getCurrentPinType() || "").toLowerCase() === "quote" &&
    getQuoteInvoiceListScope() === "all"
  ) {
    return "mixed";
  }
  return undefined;
};

export const node_apiUrl = currentApiUrl;
export const node_imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;

// export const node_apiUrl = "http://127.0.0.1:4000/api/";
// export const node_imageBaseUrl = "http://127.0.0.1:4000/upload/";

// export const node_apiUrl = "http://192.168.1.6:4000/api/";
// export const node_imageBaseUrl = "http://192.168.1.6:4000/upload";

// Axios instance
const apiClient = axios.create({
  baseURL: currentApiUrl,
});

// State/City Axios
export const stateCityApiClient = axios.create({
  baseURL: stateCityApiConfig.baseUrl,
  headers: {
    "X-CSCAPI-KEY": stateCityApiConfig.apiKey,
  },
});

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    const userData = getUserData();
    // console.log("User data:", userData);

    // Handle FormData differently
    if (config.data instanceof FormData) {
      // Extract form fields from FormData
      const formFields = {};
      const imageFiles = [];

      for (let [key, value] of config.data.entries()) {
        if (key.startsWith("image_") || key === "image" || key === "pdf") {
          // Collect image files and PDF (sent as-is; only metadata is encrypted)
          imageFiles.push({ key, file: value });
        } else {
          // Handle JSON arrays and objects properly
          try {
            // Try to parse as JSON first
            const parsedValue = JSON.parse(value);
            formFields[key] = parsedValue;
          } catch (e) {
            // If not JSON, store as string
            formFields[key] = value;
          }
        }
      }

      // Create the request structure
      const requestData = {
        authentication: `Bearer ${token || ""}`,
        input: {
          ...formFields,
          by_user: userData?.id || "",
        },
      };
      // console.log("Request data:", requestData);
      const encodedData = encryptData(requestData);

      // Create new FormData with encrypted data
      const newFormData = new FormData();
      newFormData.append("data", encodedData);

      // Add all image files back with their original keys
      imageFiles.forEach(({ key, file }) => {
        newFormData.append(key, file);
      });

      config.data = newFormData;
      // console.log("Encoded FormData:", encodedData);
      // console.log("Image files count in FormData:", imageFiles.length);
    } else {
      // Handle regular JSON data
      const requestData = {
        authentication: `Bearer ${token || ""}`,
        input: {
          ...config.data,
          by_user: userData?.id || "",
        },
      };
      // console.log("Request data:", requestData);
      const encodedData = encryptData(requestData);
      config.data = { data: encodedData };
      // console.log("Encoded request data:", encodedData);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Check if response body is encrypted (API always returns { data: "<encrypted>" })
const isEncryptedResponse = (body) =>
  body && typeof body.data === "string" && body.success === undefined;

// Response Interceptor: decrypt success responses; on error, decrypt then check auth
apiClient.interceptors.response.use(
  (response) => {
    const body = response?.data;
    if (isEncryptedResponse(body)) {
      const decrypted = decryptData(body.data);
      // console.log("Decrypted response data:", decrypted);
      if (decrypted) response.data = decrypted;
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let message = data?.message;

      if (isEncryptedResponse(data)) {
        const decrypted = decryptData(data.data);
        if (decrypted?.message) {
          message = decrypted.message;
          error.response.data = decrypted;
        }
      }

      const isAuthError =
        status === 401 ||
        (typeof message === "string" &&
          message
            .toLowerCase()
            .match(/\b(unauthorized|authentication|token)\b/));

      if (isAuthError) {
        console.warn("Authentication error detected");
        localStorage.removeItem("token");
        localStorage.removeItem("keepLoggedIn");
      }
    }
    return Promise.reject(error);
  },
);

// Auth Token Functions
export const clearAuthToken = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("keepLoggedIn");
  localStorage.removeItem("userPermissions");
  localStorage.removeItem("userData");
};

export const setAuthToken = (token, keepLoggedIn = false) => {
  if (!token) {
    console.warn("No token to store.");
    return;
  }

  const encrypted = encryptData(token);
  localStorage.setItem("token", encrypted);
  // console.log("Token encrypted and stored:", encrypted);

  if (keepLoggedIn) {
    localStorage.setItem("keepLoggedIn", "true");
  }
};

// User Data Functions
export const setUserData = (data) => {
  if (data) {
    localStorage.setItem("userData", encryptData(data));
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("branchChanged", (event) => {
    try {
      const branchId = Number(event?.detail?.branchId);
      if (Number.isFinite(branchId) && branchId > 0) {
        const current = getUserData();
        if (current && typeof current === "object") {
          setUserData({
            ...current,
            branch_id: branchId,
            branchId: branchId,
            active_branch_id: branchId,
          });
        }
      }
    } catch {
      // ignore
    }
  });
}

export const getUserData = () => {
  const encryptedData = localStorage.getItem("userData");
  return encryptedData ? decryptData(encryptedData) : null;
};

/** Legacy login uses encrypted `userData`; ERP OTP uses plain `user-details` — same merge as Navbar. */
export const mergeLegacyAndErpUser = (legacy, erpUser) => {
  if (!legacy && !erpUser) return null;
  const name =
    (legacy?.name && String(legacy.name).trim()) ||
    (erpUser?.name && String(erpUser.name).trim()) ||
    (erpUser?.full_name && String(erpUser.full_name).trim()) ||
    (erpUser?.email && String(erpUser.email).trim()) ||
    "";
  const phone =
    (legacy?.phone && String(legacy.phone).trim()) ||
    (erpUser?.phone && String(erpUser.phone).trim()) ||
    (erpUser?.mobile && String(erpUser.mobile).trim()) ||
    "";
  return {
    ...(legacy || {}),
    ...(erpUser || {}),
    name,
    phone,
    id: legacy?.id ?? erpUser?.id ?? erpUser?.user_id ?? erpUser?.ua_id,
    ua_id: legacy?.ua_id ?? erpUser?.ua_id ?? erpUser?.id,
  };
};

/** Resolved user for UI/profile: encrypted `userData` plus ERP session user when present. */
export const getMergedUserData = () => {
  const legacy = getUserData();
  const erpUser = getErpAuthState()?.user ?? null;
  return mergeLegacyAndErpUser(legacy, erpUser);
};

// User Permissions Functions
export const setUserPermissions = (permissions) => {
  if (permissions) {
    localStorage.setItem("userPermissions", encryptData(permissions));
  }
};

export function getUserPermissions() {
  const encryptedPermissions = localStorage.getItem("userPermissions");
  if (!encryptedPermissions) return null;

  const decrypted = decryptData(encryptedPermissions);
  if (!decrypted) {
    console.warn("Permissions decryption failed");
    return null;
  }

  return decrypted;
}

// Refresh current user's permissions from server
export const refreshUserPermissions = async () => {
  try {
    const userData = getUserData();
    if (!userData) return false;

    const data = await erpFetch("/show_users_admin", {
      method: "POST",
      body: { type: "show" },
    });

    const userRows = Array.isArray(data?.data) ? data.data : [];
    if (isErpApiSuccess(data) && userRows.length) {
      const currentUserData = userRows.find(
        (user) => user.ua_id === userData.id || user.ua_id === userData.ua_id,
      );

      if (currentUserData) {
        // Update permissions in localStorage
        setUserPermissions(currentUserData);
        // console.log("✅ User permissions refreshed from server");
        return true;
      }
    }
  } catch (error) {
    console.error("Error refreshing user permissions:", error);
  }
  return false;
};

// Token Expiry (JWT)
export const getTokenExpiry = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(payload));
    return decodedPayload.exp ? new Date(decodedPayload.exp * 1000) : null;
  } catch (error) {
    return null;
  }
};

// Utility Date Formatter
export const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";

  try {
    // Handle ISO datetime strings (e.g., "2025-12-18T18:30:00.000Z")
    let dateOnly = dateStr;
    if (dateStr.includes("T")) {
      dateOnly = dateStr.split("T")[0];
    }

    const [year, month, day] = dateOnly.split("-");
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error, "Input:", dateStr);
    return dateStr; // Return original if parsing fails
  }
};

export { apiClient };
